<template>
  <div class="container-fluid">
    <h1>Журнал аудита</h1>

    <div class="row mb-3">
      <div class="col-md-2">
        <label class="form-label">Таблица</label>
        <select class="form-control" v-model="params.tableName">
          <option :value="null">Все</option>
          <option v-for="t in tables" :key="t.value" :value="t.value">{{ t.label }}</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">ID записи</label>
        <input class="form-control" v-model="params.rowId">
      </div>
      <div class="col-md-2">
        <label class="form-label">Действие</label>
        <select class="form-control" v-model="params.action">
          <option :value="null">Все</option>
          <option v-for="a in actions" :key="a" :value="a">{{ a }}</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">Пользователь (ID)</label>
        <input class="form-control" v-model="params.userId">
      </div>
      <div class="col-md-2">
        <label class="form-label">С</label>
        <input type="date" class="form-control" v-model="fromDate">
      </div>
      <div class="col-md-2">
        <label class="form-label">По</label>
        <input type="date" class="form-control" v-model="toDate">
      </div>
    </div>
    <div class="mb-3">
      <button class="btn btn-primary" @click="fetch(true)" :disabled="loading">{{ loading ? "Загрузка..." : "Поиск" }}</button>
      <span class="ms-3">Найдено: {{ total }}</span>
    </div>

    <b-pagination v-model="currentPage" :total-rows="total" :per-page="params.pageSize"></b-pagination>

    <el-table :data="entries" style="width: 100%" border>
      <el-table-column type="expand">
        <template #default="props">
          <pre class="changed">{{ prettyChanged(props.row.changed) }}</pre>
        </template>
      </el-table-column>
      <el-table-column label="Когда" width="170">
        <template #default="scope">{{ formatDate(scope.row.occurredAt) }}</template>
      </el-table-column>
      <el-table-column label="Кто" width="200">
        <template #default="scope">
          <div>{{ scope.row.userName || scope.row.userId }}</div>
          <small class="text-muted">{{ scope.row.actor }} · {{ scope.row.ip }}</small>
        </template>
      </el-table-column>
      <el-table-column prop="action" label="Действие" width="120"></el-table-column>
      <el-table-column prop="tableName" label="Объект" width="230"></el-table-column>
      <el-table-column prop="rowId" label="ID" width="120"></el-table-column>
      <el-table-column prop="reason" label="Причина"></el-table-column>
      <el-table-column prop="requestId" label="Запрос" width="140">
        <template #default="scope"><small>{{ scope.row.requestId }}</small></template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { AuditLogEntry, AuditLogSearchParams, findAuditLog } from "@/services/auditService";
import { extractApiError } from "@/utils/apiError";

/** Просмотр audit_log администратором: только чтение, фильтры по объекту/пользователю/действию/периоду. */
export default defineComponent({
  name: "AuditLogView",
  setup() {
    const tables = [
      { value: "kindergarten_calculations", label: "Расчёты" },
      { value: "reports", label: "Отчёты" },
      { value: "act_reconciliations", label: "Акты сверки" },
      { value: "invoices", label: "Счета-фактуры" },
      { value: "kindergarten_act_reconciliation", label: "Акты садиков" },
      { value: "kindergarten_1c_act_reconciliation", label: "Акты сверок 1С" },
      { value: "monthly_timesheets", label: "Табели" },
      { value: "monthly_timesheet_dataset", label: "Строки табеля" },
      { value: "kindergarten_retentions", label: "Удержания" },
      { value: "kindergarten_paid_amount", label: "Оплаты" },
      { value: "lms_systems", label: "Садики (справочник)" },
      { value: "kindergarten_allowances", label: "Надбавки" },
      { value: "kindergarten_document_infos", label: "Документы садиков" },
      { value: "kindergarten_special_standards", label: "Спец. нормативы" },
      { value: "http_request", label: "Отказы в доступе" },
    ];
    const actions = ["INSERT", "UPDATE", "DELETE", "SOFT_DELETE", "RESTORE", "DENIED"];
    const params = ref<AuditLogSearchParams>({ page: 0, pageSize: 50, tableName: null, rowId: null, userId: null, action: null });
    const fromDate = ref<string | null>(null);
    const toDate = ref<string | null>(null);
    const entries = ref<AuditLogEntry[]>([]);
    const total = ref(0);
    const loading = ref(false);
    const currentPage = ref(1);

    const fetch = async (resetPage = false) => {
      if (resetPage) {
        currentPage.value = 1;
      }
      params.value.page = currentPage.value - 1;
      params.value.from = fromDate.value ? new Date(fromDate.value).toISOString() : null;
      params.value.to = toDate.value ? new Date(new Date(toDate.value).getTime() + 86_400_000).toISOString() : null;
      loading.value = true;
      try {
        const page = await findAuditLog(params.value);
        entries.value = page.content;
        total.value = page.totalElements;
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось загрузить журнал"), type: "error", showClose: true });
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (iso: string) => new Date(iso).toLocaleString("ru-RU");
    const prettyChanged = (changed: string | null) => {
      if (!changed) return "";
      try {
        return JSON.stringify(JSON.parse(changed), null, 2);
      } catch {
        return changed;
      }
    };

    watch(currentPage, () => fetch(false));
    onMounted(() => fetch(true));

    return { tables, actions, params, fromDate, toDate, entries, total, loading, currentPage, fetch, formatDate, prettyChanged };
  },
});
</script>

<style scoped>
.changed {
  white-space: pre-wrap;
  font-size: 12px;
  margin: 0;
}
</style>
